import { sharedPoll, stopPoll } from '@/command/poll'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  Search,
  Shield,
  X,
} from 'lucide-react'
import { systemFaultsApi } from '@/api/systemFaults'
import { useAuth } from '@/hooks/useAuth'
import type {
  AlertConfigRow,
  SystemFaultCategory,
  SystemFaultHistoryPoint,
  SystemFaultRow,
  SystemFaultSeverity,
  SystemFaultStatus,
  SystemFaultsOverview,
} from '@/types'

const CARD = { background: '#252836', border: '1px solid #2d3148' } as const
const MUTED = '#6b7280'
const SUB = '#9ca3af'
const ACCENT = '#6366f1'

const SEVERITY_ORDER: Record<SystemFaultSeverity, number> = { p0: 0, p1: 1, p2: 2, p3: 3 }

const CATEGORY_LABEL: Record<SystemFaultCategory, string> = {
  http_error: 'שגיאות HTTP',
  cron_failure: 'כשלי cron',
  pg_error: 'שגיאות Postgres',
  security_advisor: 'אבטחה',
  performance_advisor: 'ביצועים',
}

const STATUS_LABEL: Record<SystemFaultStatus, string> = {
  open: 'פתוח',
  acknowledged: 'טופל',
  muted: 'מושתק',
  resolved: 'נסגר',
}

const ALERT_LABEL: Record<string, string> = {
  fault_p0_new: 'תקלת P0 חדשה',
  fault_volume_spike: 'קפיצת נפח',
  cron_failures: 'כשלי cron',
  security_advisor_new: 'Advisor אבטחה חדש',
}

function severityStyle(sev: SystemFaultSeverity): { bg: string; color: string } {
  switch (sev) {
    case 'p0':
      return { bg: '#ef444430', color: '#f87171' }
    case 'p1':
      return { bg: '#f9731630', color: '#fb923c' }
    case 'p2':
      return { bg: '#eab30830', color: '#fbbf24' }
    default:
      return { bg: '#6366f130', color: '#a5b4fc' }
  }
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('he-IL')
  } catch {
    return iso
  }
}

function minutesAgo(iso: string | null): number | null {
  if (!iso) return null
  const ms = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ms)) return null
  return Math.max(0, Math.round(ms / 60_000))
}

function recipientsEmpty(recipients: string[] | null | undefined): boolean {
  if (!recipients || recipients.length === 0) return true
  return recipients.every((r) => !r || !String(r).trim())
}

function isAdvisor(c: SystemFaultCategory): boolean {
  return c === 'security_advisor' || c === 'performance_advisor'
}

/** Reopened: prior resolved twin same fingerprint, or metadata flag, or first_seen ≫ recent activity. */
function isReopened(fault: SystemFaultRow, all: SystemFaultRow[]): boolean {
  if (fault.metadata && fault.metadata.reopened === true) return true
  if (all.some((f) => f.id !== fault.id && f.fingerprint === fault.fingerprint && f.status === 'resolved')) {
    return true
  }
  if (fault.status === 'resolved') return false
  const first = new Date(fault.first_seen_at).getTime()
  const last = new Date(fault.last_seen_at).getTime()
  // first_seen much older than a typical open window (3×15m) while still recently active
  return last - first > 3 * 24 * 60 * 60_000 && Date.now() - last < 2 * 60 * 60_000
}

function Sparkline({ points, width = 72, height = 22 }: { points: number[]; width?: number; height?: number }) {
  if (points.length < 2) {
    return <span className="font-mono text-[10px]" style={{ color: MUTED }}>—</span>
  }
  const max = Math.max(...points, 1)
  const min = Math.min(...points, 0)
  const span = Math.max(max - min, 1)
  const coords = points
    .map((v, i) => {
      const x = (i / (points.length - 1)) * width
      const y = height - ((v - min) / span) * (height - 2) - 1
      return `${x},${y}`
    })
    .join(' ')
  return (
    <svg width={width} height={height} className="inline-block align-middle" aria-hidden>
      <polyline fill="none" stroke={ACCENT} strokeWidth="1.5" points={coords} />
    </svg>
  )
}

function JsonBlock({ value }: { value: unknown }) {
  const text = useMemo(() => {
    try {
      return JSON.stringify(value ?? {}, null, 2)
    } catch {
      return String(value)
    }
  }, [value])
  return (
    <pre
      className="text-[11px] font-mono overflow-x-auto rounded-lg p-3 whitespace-pre-wrap break-words"
      style={{ background: '#1a1d27', border: '1px solid #2d3148', color: '#d1d5db', maxHeight: 280 }}
    >
      {text}
    </pre>
  )
}

export default function SystemFaultsTab() {
  const { user, isSuperAdmin } = useAuth()
  const canWrite = isSuperAdmin
  const [searchParams, setSearchParams] = useSearchParams()

  const severityFilter = (searchParams.get('severity') as SystemFaultSeverity | 'all' | null) || 'all'
  const statusFilter = (searchParams.get('status') as SystemFaultStatus | 'all' | null) || 'all'
  const categoryFilter = (searchParams.get('category') as SystemFaultCategory | 'all' | null) || 'all'
  const q = searchParams.get('q') ?? ''

  const setFilter = useCallback(
    (key: string, value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('tab', 'faults')
          if (!value || value === 'all') next.delete(key)
          else next.set(key, value)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const [overview, setOverview] = useState<SystemFaultsOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sparklines, setSparklines] = useState<Record<number, number[]>>({})
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [drawerHistory, setDrawerHistory] = useState<SystemFaultHistoryPoint[]>([])
  const [muteNote, setMuteNote] = useState('')
  const [showMuteInput, setShowMuteInput] = useState(false)
  const [notesDraft, setNotesDraft] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)
  const [advisorOpen, setAdvisorOpen] = useState(false)
  const [mutedOpen, setMutedOpen] = useState(false)
  const [alertRows, setAlertRows] = useState<AlertConfigRow[]>([])
  const [alertError, setAlertError] = useState<string | null>(null)

  const fetchOverview = useCallback(async () => {
    if (document.visibilityState === 'hidden') return
    try {
      const data = await systemFaultsApi.overview(true)
      setOverview(data)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בטעינה')
      return false
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchAlerts = useCallback(async () => {
    const rows = await systemFaultsApi.listFaultAlertConfig()
    setAlertRows(rows)
    if (rows.length === 0) {
      setAlertError('לא ניתן לקרוא את alert_config (RLS / עדיין לא מיושם בבקאנד)')
    } else {
      setAlertError(null)
    }
  }, [])

  useEffect(() => {
    const poll = sharedPoll(fetchOverview, 60_000, true)
    const alerts = sharedPoll(fetchAlerts, 300000, true)
    return () => {
      stopPoll(poll)
      stopPoll(alerts)
    }
  }, [fetchOverview, fetchAlerts])

  const faults = overview?.open_faults ?? []

  useEffect(() => {
    const ids = faults.filter((f) => !isAdvisor(f.category)).map((f) => f.id).slice(0, 80)
    if (ids.length === 0) return
    let cancelled = false
    ;(async () => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          const series = await systemFaultsApi.historySeries(id, 1)
          return [id, series.map((p) => p.occurrences_in_window)] as const
        }),
      )
      if (cancelled) return
      setSparklines((prev) => {
        const next = { ...prev }
        for (const [id, pts] of entries) next[id] = pts
        return next
      })
    })()
    return () => {
      cancelled = true
    }
  }, [faults])

  const selected = useMemo(
    () => faults.find((f) => f.id === selectedId) ?? null,
    [faults, selectedId],
  )

  useEffect(() => {
    if (!selected) {
      setDrawerHistory([])
      return
    }
    setNotesDraft(selected.notes ?? '')
    setShowMuteInput(false)
    setMuteNote('')
    setActionError(null)
    let cancelled = false
    systemFaultsApi.historySeries(selected.id, 7).then((pts) => {
      if (!cancelled) setDrawerHistory(pts)
    })
    return () => {
      cancelled = true
    }
  }, [selected?.id])

  const applyOptimistic = useCallback((id: number, patch: Partial<SystemFaultRow>) => {
    setOverview((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        open_faults: prev.open_faults.map((f) => (f.id === id ? { ...f, ...patch } : f)),
      }
    })
  }, [])

  const runAction = useCallback(
    async (id: number, optimistic: Partial<SystemFaultRow>, fn: () => Promise<void>) => {
      if (!canWrite) {
        setActionError('פעולה זמינה למנהלי פלטפורמה בלבד')
        return
      }
      const prev = faults.find((f) => f.id === id)
      applyOptimistic(id, optimistic)
      try {
        await fn()
        setActionError(null)
        await fetchOverview()
      } catch (e) {
        if (prev) applyOptimistic(id, prev)
        setActionError(e instanceof Error ? e.message : 'הפעולה נכשלה — ייתכן שחסר GRANT UPDATE בבקאנד')
      }
    },
    [applyOptimistic, canWrite, faults, fetchOverview],
  )

  const counts = overview?.counts_by_severity ?? {}
  const openP0 = counts.p0 ?? 0
  const openP1 = counts.p1 ?? 0
  const openP2P3 = (counts.p2 ?? 0) + (counts.p3 ?? 0)
  const totalOpen =
    (overview?.counts_by_status?.open ?? 0) +
    (overview?.counts_by_status?.acknowledged ?? 0) +
    (overview?.counts_by_status?.muted ?? 0)
  const mins = minutesAgo(overview?.last_scan_at ?? null)
  const scanStale = mins != null && mins > 45

  const filtered = useMemo(() => {
    let rows = [...faults]
    if (severityFilter !== 'all') rows = rows.filter((f) => f.severity === severityFilter)
    if (statusFilter !== 'all') rows = rows.filter((f) => f.status === statusFilter)
    if (categoryFilter !== 'all') rows = rows.filter((f) => f.category === categoryFilter)
    const needle = q.trim().toLowerCase()
    if (needle) {
      rows = rows.filter(
        (f) =>
          f.title.toLowerCase().includes(needle) ||
          (f.detail ?? '').toLowerCase().includes(needle),
      )
    }
    rows.sort(
      (a, b) =>
        SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] ||
        new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime(),
    )
    return rows
  }, [faults, severityFilter, statusFilter, categoryFilter, q])

  const mainRows = filtered.filter((f) => !isAdvisor(f.category) && f.status !== 'muted')
  const mutedRows = filtered.filter((f) => !isAdvisor(f.category) && f.status === 'muted')
  const advisorRows = filtered.filter((f) => isAdvisor(f.category))

  const advisorGroups = useMemo(() => {
    const map = new Map<string, { lint: string; count: number; rows: SystemFaultRow[] }>()
    for (const f of advisorRows) {
      const lint =
        (typeof f.metadata?.lint === 'string' && f.metadata.lint) ||
        (typeof f.metadata?.name === 'string' && f.metadata.name) ||
        f.title
      const cur = map.get(lint) ?? { lint, count: 0, rows: [] }
      cur.count += 1
      cur.rows.push(f)
      map.set(lint, cur)
    }
    return [...map.values()].sort((a, b) => b.count - a.count)
  }, [advisorRows])

  const emptyOpen =
    !loading &&
    mainRows.length === 0 &&
    (statusFilter === 'all' || statusFilter === 'open') &&
    !q &&
    severityFilter === 'all' &&
    categoryFilter === 'all'

  const selectClass =
    'rounded-lg px-2 py-1.5 text-xs bg-[#1a1d27] border border-[#2d3148] text-gray-200'

  return (
    <div className="space-y-5" dir="rtl">
      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { label: 'P0 פתוחים', value: openP0, dominant: true, color: '#ef4444' },
          { label: 'P1 פתוחים', value: openP1, color: '#fb923c' },
          { label: 'P2+P3 פתוחים', value: openP2P3, color: '#fbbf24' },
          { label: 'סה״כ פתוחים', value: totalOpen, color: totalOpen ? '#f87171' : '#4ade80' },
          {
            label: 'סריקה אחרונה',
            value: mins == null ? '—' : `לפני ${mins} דק׳`,
            color: scanStale ? '#f87171' : SUB,
            mono: false,
          },
        ].map((kpi) => (
          <div
            key={kpi.label}
            className="rounded-xl p-4"
            style={{
              ...CARD,
              ...(kpi.dominant
                ? { border: '1px solid #ef444480', boxShadow: '0 0 0 1px #ef444422 inset' }
                : {}),
            }}
          >
            <div className="text-[11px]" style={{ color: MUTED }}>
              {kpi.label}
            </div>
            <div
              className={`mt-1 font-semibold ${kpi.mono === false ? 'text-sm' : 'text-2xl font-mono'}`}
              style={{ color: kpi.color }}
            >
              {kpi.value}
            </div>
          </div>
        ))}
      </div>

      {scanStale && (
        <div
          className="flex items-center gap-2 text-xs px-3 py-2.5 rounded-lg"
          style={{ background: '#eab30818', color: '#fbbf24', border: '1px solid #eab30840' }}
        >
          <AlertTriangle size={14} />
          הסורק לא רץ — ייתכן שהנתונים לא מעודכנים
        </div>
      )}

      {/* Filters */}
      <div className="rounded-xl p-3 flex flex-wrap gap-2 items-center" style={CARD}>
        <select
          className={selectClass}
          value={severityFilter}
          onChange={(e) => setFilter('severity', e.target.value)}
        >
          <option value="all">חומרה · הכל</option>
          {(['p0', 'p1', 'p2', 'p3'] as const).map((s) => (
            <option key={s} value={s}>
              {s.toUpperCase()}
            </option>
          ))}
        </select>
        <select
          className={selectClass}
          value={statusFilter}
          onChange={(e) => setFilter('status', e.target.value)}
        >
          <option value="all">סטטוס · הכל</option>
          {(Object.keys(STATUS_LABEL) as SystemFaultStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <select
          className={selectClass}
          value={categoryFilter}
          onChange={(e) => setFilter('category', e.target.value)}
        >
          <option value="all">קטגוריה · הכל</option>
          {(Object.keys(CATEGORY_LABEL) as SystemFaultCategory[]).map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABEL[c]}
            </option>
          ))}
        </select>
        <div className="flex-1 min-w-[160px] relative">
          <Search size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2" style={{ color: MUTED }} />
          <input
            className="w-full rounded-lg pr-8 pl-2 py-1.5 text-xs bg-[#1a1d27] border border-[#2d3148] text-gray-200"
            placeholder="חיפוש בכותרת / פירוט…"
            value={q}
            onChange={(e) => setFilter('q', e.target.value)}
          />
        </div>
      </div>

      {error && (
        <p className="text-xs" style={{ color: '#f87171' }}>
          {error}
        </p>
      )}

      {/* Main table */}
      <div className="rounded-xl p-4 overflow-x-auto" style={CARD}>
        <h2 className="text-sm font-semibold text-white mb-3">תקלות</h2>
        {loading ? (
          <p className="text-sm" style={{ color: MUTED }}>
            טוען תקלות…
          </p>
        ) : emptyOpen ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <CheckCircle2 size={36} style={{ color: '#4ade80' }} />
            <p className="text-sm text-white">לא נמצאו תקלות פתוחות</p>
          </div>
        ) : mainRows.length === 0 ? (
          <p className="text-sm py-6 text-center" style={{ color: MUTED }}>
            אין תוצאות לסינון הנוכחי
          </p>
        ) : (
          <table className="w-full text-xs text-right">
            <thead>
              <tr style={{ color: MUTED }}>
                <th className="py-2 font-medium">חומרה</th>
                <th className="py-2 font-medium">כותרת</th>
                <th className="py-2 font-medium">קטגוריה</th>
                <th className="py-2 font-medium">שיוך</th>
                <th className="py-2 font-medium">סריקה</th>
                <th className="py-2 font-medium">סה״כ</th>
                <th className="py-2 font-medium">24 שע׳</th>
                <th className="py-2 font-medium">ראשון</th>
                <th className="py-2 font-medium">אחרון</th>
                <th className="py-2 font-medium">סטטוס</th>
              </tr>
            </thead>
            <tbody>
              {mainRows.map((f) => {
                const sev = severityStyle(f.severity)
                const reopened = isReopened(f, faults)
                return (
                  <tr
                    key={f.id}
                    onClick={() => setSelectedId(f.id)}
                    className="cursor-pointer hover:bg-white/[0.03]"
                    style={{ borderTop: '1px solid #2d3148', color: '#d1d5db' }}
                  >
                    <td className="py-2.5">
                      <span
                        className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase"
                        style={{ background: sev.bg, color: sev.color }}
                      >
                        {f.severity}
                      </span>
                    </td>
                    <td className="py-2.5 max-w-[220px]">
                      <span className="text-white">{f.title}</span>
                      {reopened && (
                        <span
                          className="ms-2 text-[10px] px-1.5 py-0.5 rounded"
                          style={{ background: '#6366f130', color: '#a5b4fc' }}
                        >
                          נפתח מחדש
                        </span>
                      )}
                    </td>
                    <td className="py-2.5">{CATEGORY_LABEL[f.category] ?? f.category}</td>
                    <td className="py-2.5 font-mono text-[11px]">{f.attribution ?? '—'}</td>
                    <td className="py-2.5 font-mono">{f.occurrences_last_scan}</td>
                    <td className="py-2.5 font-mono">{f.occurrences}</td>
                    <td className="py-2.5">
                      <Sparkline points={sparklines[f.id] ?? []} />
                    </td>
                    <td className="py-2.5 whitespace-nowrap">{fmtTime(f.first_seen_at)}</td>
                    <td className="py-2.5 whitespace-nowrap">{fmtTime(f.last_seen_at)}</td>
                    <td className="py-2.5">{STATUS_LABEL[f.status]}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Muted section */}
      {mutedRows.length > 0 && (
        <div className="rounded-xl overflow-hidden" style={CARD}>
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 text-sm text-white"
            onClick={() => setMutedOpen((v) => !v)}
          >
            <span>מושתקים ({mutedRows.length})</span>
            {mutedOpen ? <ChevronDown size={16} /> : <ChevronLeft size={16} />}
          </button>
          {mutedOpen && (
            <div className="px-4 pb-4 overflow-x-auto">
              <table className="w-full text-xs text-right">
                <tbody>
                  {mutedRows.map((f) => (
                    <tr
                      key={f.id}
                      onClick={() => setSelectedId(f.id)}
                      className="cursor-pointer"
                      style={{ borderTop: '1px solid #2d3148', color: SUB }}
                    >
                      <td className="py-2">{f.severity.toUpperCase()}</td>
                      <td className="py-2 text-white">{f.title}</td>
                      <td className="py-2">{CATEGORY_LABEL[f.category]}</td>
                      <td className="py-2">{STATUS_LABEL[f.status]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Advisor findings */}
      {advisorGroups.length > 0 && (
        <div className="rounded-xl overflow-hidden" style={CARD}>
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 text-sm text-white"
            onClick={() => setAdvisorOpen((v) => !v)}
          >
            <span className="flex items-center gap-2">
              <Shield size={14} style={{ color: ACCENT }} />
              ממצאי Advisor ({advisorRows.length})
            </span>
            {advisorOpen ? <ChevronDown size={16} /> : <ChevronLeft size={16} />}
          </button>
          {advisorOpen && (
            <div className="px-4 pb-4 space-y-2">
              {advisorGroups.map((g) => (
                <div key={g.lint} className="rounded-lg px-3 py-2" style={{ background: '#1a1d27' }}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-white">{g.lint}</span>
                    <span style={{ color: MUTED }}>{g.count}</span>
                  </div>
                  <div className="mt-1 space-y-1">
                    {g.rows.slice(0, 8).map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        className="block w-full text-right text-[11px] py-0.5"
                        style={{ color: SUB }}
                        onClick={() => setSelectedId(f.id)}
                      >
                        {f.title}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Alert settings */}
      <div className="rounded-xl p-4 space-y-3" style={CARD}>
        <h2 className="text-sm font-semibold text-white">הגדרות התראות</h2>
        {alertError && (
          <p className="text-[11px]" style={{ color: '#fbbf24' }}>
            {alertError}
          </p>
        )}
        {alertRows.length === 0 && !alertError ? (
          <p className="text-xs" style={{ color: MUTED }}>
            אין כללי התראת תקלות
          </p>
        ) : (
          <div className="space-y-3">
            {alertRows.map((row) => {
              const emptyRecipients = recipientsEmpty(row.recipients)
              const warn = row.enabled && emptyRecipients
              return (
                <div
                  key={row.alert_type}
                  className="rounded-lg p-3 space-y-2"
                  style={{ background: '#1a1d27', border: warn ? '1px solid #ef444460' : '1px solid #2d3148' }}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-xs text-white font-medium">
                        {ALERT_LABEL[row.alert_type] ?? row.alert_type}
                      </div>
                      <div className="text-[10px] font-mono mt-0.5" style={{ color: MUTED }}>
                        {row.alert_type} · {row.channel} · cooldown {row.cooldown_secs}s
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs" style={{ color: SUB }}>
                      <input
                        type="checkbox"
                        checked={row.enabled}
                        disabled={!canWrite}
                        onChange={async (e) => {
                          const enabled = e.target.checked
                          setAlertRows((prev) =>
                            prev.map((r) => (r.alert_type === row.alert_type ? { ...r, enabled } : r)),
                          )
                          try {
                            await systemFaultsApi.updateAlertConfig(row.alert_type, { enabled })
                          } catch {
                            setAlertRows((prev) =>
                              prev.map((r) =>
                                r.alert_type === row.alert_type ? { ...r, enabled: row.enabled } : r,
                              ),
                            )
                          }
                        }}
                      />
                      פעיל
                    </label>
                  </div>
                  {warn && (
                    <div className="text-[11px] px-2 py-1.5 rounded" style={{ background: '#ef444420', color: '#f87171' }}>
                      התראה פעילה ללא נמען — לא יישלח דבר
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label className="text-[11px] space-y-1" style={{ color: MUTED }}>
                      סף (threshold)
                      <input
                        type="number"
                        className={selectClass + ' w-full'}
                        disabled={!canWrite}
                        defaultValue={row.threshold ?? ''}
                        key={`th-${row.alert_type}-${row.threshold}`}
                        onBlur={async (e) => {
                          const threshold = e.target.value === '' ? null : Number(e.target.value)
                          if (threshold === row.threshold) return
                          try {
                            await systemFaultsApi.updateAlertConfig(row.alert_type, { threshold })
                            setAlertRows((prev) =>
                              prev.map((r) =>
                                r.alert_type === row.alert_type ? { ...r, threshold } : r,
                              ),
                            )
                          } catch {
                            /* keep UI; fetchAlerts next poll */
                          }
                        }}
                      />
                    </label>
                    <label className="text-[11px] space-y-1" style={{ color: MUTED }}>
                      נמענים (פסיק בין מספרים)
                      <input
                        className={selectClass + ' w-full'}
                        disabled={!canWrite}
                        defaultValue={(row.recipients ?? []).join(', ')}
                        key={`rc-${row.alert_type}-${(row.recipients ?? []).join(',')}`}
                        onBlur={async (e) => {
                          const recipients = e.target.value
                            .split(',')
                            .map((s) => s.trim())
                            .filter(Boolean)
                          try {
                            await systemFaultsApi.updateAlertConfig(row.alert_type, { recipients })
                            setAlertRows((prev) =>
                              prev.map((r) =>
                                r.alert_type === row.alert_type ? { ...r, recipients } : r,
                              ),
                            )
                          } catch {
                            /* ignore */
                          }
                        }}
                      />
                    </label>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Side drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-start">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="סגור"
            onClick={() => setSelectedId(null)}
          />
          <div
            className="relative h-full w-full max-w-md overflow-y-auto shadow-2xl p-5 space-y-4"
            style={{ background: '#1a1d27', borderLeft: '1px solid #2d3148' }}
            dir="rtl"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase"
                    style={{
                      background: severityStyle(selected.severity).bg,
                      color: severityStyle(selected.severity).color,
                    }}
                  >
                    {selected.severity}
                  </span>
                  {isReopened(selected, faults) && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: '#6366f130', color: '#a5b4fc' }}>
                      נפתח מחדש
                    </span>
                  )}
                </div>
                <h3 className="text-base font-semibold text-white">{selected.title}</h3>
                <p className="text-[11px] mt-1" style={{ color: MUTED }}>
                  {CATEGORY_LABEL[selected.category]} · {STATUS_LABEL[selected.status]}
                </p>
              </div>
              <button type="button" onClick={() => setSelectedId(null)} style={{ color: MUTED }}>
                <X size={18} />
              </button>
            </div>

            <div className="text-xs space-y-1" style={{ color: SUB }}>
              <div>
                <span style={{ color: MUTED }}>מקור: </span>
                {selected.source ?? '—'}
              </div>
              <div>
                <span style={{ color: MUTED }}>ביטחון: </span>
                {selected.confidence ?? '—'}
              </div>
              <div>
                <span style={{ color: MUTED }}>שיוך: </span>
                {selected.attribution ?? '—'}
              </div>
            </div>

            <div>
              <div className="text-[11px] mb-1" style={{ color: MUTED }}>
                פירוט
              </div>
              <p className="text-xs whitespace-pre-wrap" style={{ color: '#d1d5db' }}>
                {selected.detail ?? '—'}
              </p>
            </div>

            <div>
              <div className="text-[11px] mb-1" style={{ color: MUTED }}>
                metadata
                {selected.metadata?.cadence != null ? ' · cadence (דפוס דקה בשעה)' : ''}
              </div>
              <JsonBlock value={selected.metadata} />
            </div>

            <div>
              <div className="text-[11px] mb-1" style={{ color: MUTED }}>
                היסטוריית הופעות (7 ימים)
              </div>
              <div className="py-2">
                <Sparkline
                  points={drawerHistory.map((p) => p.occurrences_in_window)}
                  width={320}
                  height={48}
                />
              </div>
              <div className="text-[10px] font-mono" style={{ color: MUTED }}>
                {drawerHistory.length} נקודות · ראשון {fmtTime(selected.first_seen_at)} · אחרון{' '}
                {fmtTime(selected.last_seen_at)}
              </div>
            </div>

            <div>
              <div className="text-[11px] mb-1" style={{ color: MUTED }}>
                הערות
              </div>
              <textarea
                className="w-full rounded-lg p-2 text-xs bg-[#252836] border border-[#2d3148] text-gray-200 min-h-[72px]"
                value={notesDraft}
                disabled={!canWrite}
                onChange={(e) => setNotesDraft(e.target.value)}
                onBlur={() => {
                  if (!canWrite || notesDraft === (selected.notes ?? '')) return
                  void runAction(selected.id, { notes: notesDraft }, () =>
                    systemFaultsApi.saveNotes(selected.id, notesDraft),
                  )
                }}
              />
            </div>

            {actionError && (
              <p className="text-[11px]" style={{ color: '#f87171' }}>
                {actionError}
              </p>
            )}

            <div className="flex flex-wrap gap-2 pt-2" style={{ borderTop: '1px solid #2d3148' }}>
              <button
                type="button"
                disabled={!canWrite || selected.status === 'acknowledged'}
                className="px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-40"
                style={{ background: '#6366f130', color: '#a5b4fc', border: '1px solid #6366f150' }}
                onClick={() => {
                  if (!user?.auth_user_id && !user?.id) {
                    setActionError('חסר מזהה משתמש')
                    return
                  }
                  const uid = user.auth_user_id || user.id
                  void runAction(
                    selected.id,
                    { status: 'acknowledged', acknowledged_by: uid },
                    () => systemFaultsApi.acknowledge(selected.id, uid),
                  )
                }}
              >
                אישור טיפול
              </button>
              <button
                type="button"
                disabled={!canWrite || selected.status === 'muted'}
                className="px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-40"
                style={{ background: '#eab30820', color: '#fbbf24', border: '1px solid #eab30840' }}
                onClick={() => setShowMuteInput(true)}
              >
                השתקה
              </button>
              <button
                type="button"
                disabled={!canWrite || selected.status === 'resolved'}
                className="px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-40"
                style={{ background: '#22c55e20', color: '#4ade80', border: '1px solid #22c55e40' }}
                onClick={() => {
                  void runAction(
                    selected.id,
                    { status: 'resolved', resolved_at: new Date().toISOString() },
                    () => systemFaultsApi.resolve(selected.id),
                  )
                }}
              >
                סגירה ידנית
              </button>
            </div>

            {showMuteInput && (
              <div className="space-y-2">
                <p className="text-[11px]" style={{ color: MUTED }}>
                  נדרשת הערה להשתקה
                </p>
                <textarea
                  className="w-full rounded-lg p-2 text-xs bg-[#252836] border border-[#2d3148] text-gray-200 min-h-[56px]"
                  value={muteNote}
                  onChange={(e) => setMuteNote(e.target.value)}
                  placeholder="סיבת ההשתקה…"
                />
                <button
                  type="button"
                  disabled={!muteNote.trim()}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-40"
                  style={{ background: '#eab30830', color: '#fbbf24' }}
                  onClick={() => {
                    const note = muteNote.trim()
                    if (!note) return
                    void runAction(selected.id, { status: 'muted', notes: note }, () =>
                      systemFaultsApi.mute(selected.id, note),
                    ).then(() => {
                      setShowMuteInput(false)
                      setMuteNote('')
                    })
                  }}
                >
                  אשר השתקה
                </button>
              </div>
            )}

            {!canWrite && (
              <p className="text-[10px]" style={{ color: MUTED }}>
                כתיבה מוגבלת למנהלי פלטפורמה (is_super_admin)
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
